/* Centrální katalog institucí — jediný zdroj pravdy o tom, „u koho" klient produkt má.
 *
 * PROČ TADY: do teď existovaly TŘI nezávislé seznamy, každý jinak psaný:
 *   • frontend/src/pages/AnalyzaV2Page.js       — const INSTITUCE (datalist v analýze)
 *   • frontend/src/components/crm/ContractEditor.js — const COMMON_PROVIDERS (datalist ve složce)
 *   • frontend/src/data/bjKatalog.js            — pole `partner` (interní provizní katalog BJ)
 * Poradce tak napsal „ČSOB penzijní" v analýze a „ČSOB Penzijní" ve složce a slučování
 * produktů (dedupKlic v lib/produkty/model.js) je vidělo jako dvě různé instituce.
 *
 * CO TENHLE MODUL NEDĚLÁ:
 *   • Netvrdí, že instituce NENÍ partner. `partnerStatus: null` znamená VÝHRADNĚ
 *     „nemáme doložený interní zdroj", nikdy „nespolupracujeme".
 *   • Nevymýšlí právní názvy. `legalName` je nejúplnější název DOLOŽENÝ v repu;
 *     když takový není, je null. NENÍ to ověřená obchodní firma z obchodního rejstříku
 *     (chybí právní forma, IČO). Kdo potřebuje právně přesný název, musí ho doplnit
 *     z ARES — viz docs/product-data/CONTRACT.md.
 *
 * PARTNERSTVÍ — TVRDÉ PRAVIDLO:
 *   `partnerStatus` se smí vyplnit jen tam, kde na to existuje doložený interní zdroj.
 *   Jediný takový zdroj v repu je dnes frontend/src/data/bjKatalog.js (hlavička souboru:
 *   „AUTO-GENEROVÁNO z katalogu BJ (20260501). Provizní engine…", položky mají pole
 *   `partner`). Pro každou instituci s partnerStatusem je v partnerStatusSource uvedený
 *   PŘESNÝ řetězec, který v tom katalogu stojí. Test hlídá, že status bez zdroje neexistuje.
 */

/** Kategorie institucí — kdo to je. */
export const KATEGORIE_INSTITUCI = [
  "bank",
  "buildingSociety",
  "pensionCompany",
  "insuranceCompany",
  "investmentCompany",
  "securitiesBroker",
  "leasingCompany",
  "creditInstitution",
  "other",
];

/** Kategorie produktů — co se u ní dá sjednat. */
export const KATEGORIE_PRODUKTU = [
  "stavebni_sporeni",
  "penze",
  "dip",
  "investice",
  "bankovni_ucty",
  "pojisteni",
  "hypoteky_uvery",
  "leasing",
];

/**
 * Typ produktu → povolené kategorie institucí.
 * Tohle je AUTORITA našeptávače: u stavebního spoření nesmí vyskočit pojišťovna.
 *
 * `penze` = doplňkové penzijní spoření (DPS) a staré penzijní připojištění. Ta
 * smí ze zákona vést VÝHRADNĚ penzijní společnost — proto jediná kategorie.
 *
 * `dip` = dlouhodobý investiční produkt. To NENÍ produkt penzijní společnosti,
 * ale daňový režim, do kterého smí svou nabídku obalit i banka, obchodník
 * s cennými papíry a investiční společnost. Do 8/2026 tady DIP mapoval na
 * `penze`, takže poradci na schůzce nabídl devět penzijních společností a
 * skutečného poskytovatele (banka, OCP) mezi nimi neměl — musel ho doťukat
 * ručně, pokaždé trochu jinak, a slučování se složkou klienta pak tutéž
 * instituci vidělo jako dvě různé.
 */
export const POVOLENE_KATEGORIE = {
  stavebni_sporeni: ["buildingSociety"],
  penze: ["pensionCompany"],
  dip: ["bank", "investmentCompany", "securitiesBroker", "pensionCompany"],
  investice: ["bank", "investmentCompany", "securitiesBroker"],
  bankovni_ucty: ["bank"],
  pojisteni: ["insuranceCompany"],
  hypoteky_uvery: ["bank", "buildingSociety", "creditInstitution"],
  leasing: ["leasingCompany"],
};

/**
 * Které produktové kategorie u instituce stačí k tomu, aby se pro danou
 * kategorii nabídla. Default = stejnojmenná kategorie.
 *
 * Výjimka `dip`: „dip" nemá v productCategories žádná instituce — DIP je režim
 * NAD existující nabídkou, ne samostatný produkt. Kvalifikuje se tedy ten, kdo
 * podle katalogu vede penzijní NEBO investiční produkty. Díky tomu se do
 * nabídky nedostane banka, o které katalog ví jen běžné účty a hypotéky
 * (mBank, Air Bank) — to by bylo tvrzení bez zdroje.
 */
export const POKRYTI_KATEGORIE = {
  dip: ["penze", "investice"],
};

/**
 * Typ produktu tak, jak ho píše zbytek app (lib/produkty/model.js TYPY, analýza V2),
 * → kategorie produktu v tomhle katalogu.
 *
 * `null` = typ, který schválně nefiltruje (jine). Typ, který tady NENÍ, je NEZNÁMÝ —
 * viz kategorieProTyp(): neznámý typ se nefiltruje a hlásí se jako neznámý,
 * NEsmí se tiše chovat jako „nic nevyhovuje".
 */
export const TYP_PRODUKTU_NA_KATEGORII = {
  // lib/produkty/model.js TYPY
  stavebni: "stavebni_sporeni",
  penzijko: "penze",
  investice: "investice",
  hypoteka: "hypoteky_uvery",
  uver: "hypoteky_uvery",
  pojisteni_zivot: "pojisteni",
  pojisteni_auta: "pojisteni",
  pojisteni_majetek: "pojisteni",
  pojisteni_odpovednost: "pojisteni",
  pojisteni_ostatni: "pojisteni",
  zdravotni: "pojisteni",
  jine: null,
  // Pojmy z analýzy a ze zadání kontraktu
  stavebni_sporeni: "stavebni_sporeni",
  dps: "penze",
  // DIP má vlastní kategorii, ne „penze" — viz komentář u POVOLENE_KATEGORIE.
  dip: "dip",
  penzijni_pripojisteni: "penze",
  penze: "penze",
  pojisteni: "pojisteni",
  bankovni_ucty: "bankovni_ucty",
  sporeni: "bankovni_ucty",
  hypoteky_uvery: "hypoteky_uvery",
  leasing: "leasing",
};

/* ——— zdroj partnerství ——— */
const BJ = "frontend/src/data/bjKatalog.js (interní katalog BJ 20260501, pole partner)";
const bjZdroj = (label) => `${BJ}: „${label}“`;

const I = (o) => ({
  institutionId: o.id,
  legalName: o.legalName ?? null,
  displayName: o.name,
  institutionCategories: o.cats,
  productCategories: o.produkty,
  active: o.active !== false,
  country: o.country || "CZ",
  partnerStatus: o.partner ? "partner" : null,
  partnerStatusSource: o.partner ? bjZdroj(o.partner) : null,
  aliases: o.aliases || [],
  poznamka: o.poznamka || null,
});

/**
 * Katalog. Jen instituce, které jsou reálně v datech repa (BJ katalog, seznamy v analýze
 * a ve složce, penzijní fondy v lib/penze/fondy.js, fondyKatalog.js), případně notoricky
 * známé CZ/SK instituce ze stejných seznamů. Nic vymyšleného.
 */
export const INSTITUCE = [
  /* ——— Stavební spořitelny ——— */
  I({ id: "modra-pyramida", name: "Modrá pyramida", legalName: "Modrá pyramida stavební spořitelna",
      cats: ["buildingSociety"], produkty: ["stavebni_sporeni", "hypoteky_uvery"],
      partner: "Modrá pyramida", aliases: ["MPSS", "Modrá pyramida stavební spořitelna"] }),
  I({ id: "rsts", name: "Raiffeisen stavební spořitelna", legalName: "Raiffeisen stavební spořitelna",
      cats: ["buildingSociety"], produkty: ["stavebni_sporeni", "hypoteky_uvery"],
      partner: "RSTS", aliases: ["RSTS", "Raiffeisen stavební spořitelna (RSTS)"] }),
  I({ id: "burinka", name: "Stavební spořitelna České spořitelny (Buřinka)",
      legalName: "Stavební spořitelna České spořitelny",
      cats: ["buildingSociety"], produkty: ["stavebni_sporeni", "hypoteky_uvery"],
      partner: "SSČS - Buřinka", aliases: ["Buřinka", "SSČS", "Stavební spořitelna ČS", "Stavební spořitelna ČS (Buřinka)"] }),
  I({ id: "csob-stavebni", name: "ČSOB Stavební spořitelna",
      cats: ["buildingSociety"], produkty: ["stavebni_sporeni", "hypoteky_uvery"],
      aliases: ["ČMSS", "CMSS", "Českomoravská stavební spořitelna", "Liška"],
      poznamka: "Aliasy ČMSS/Liška jsou starší označení, která poradci pořád píší do smluv." }),
  I({ id: "moneta-stavebni", name: "MONETA Stavební spořitelna",
      cats: ["buildingSociety"], produkty: ["stavebni_sporeni", "hypoteky_uvery"],
      aliases: ["Moneta stavební spořitelna"] }),
  I({ id: "wuestenrot-sk", name: "Wüstenrot", country: "SK",
      cats: ["buildingSociety"], produkty: ["stavebni_sporeni", "hypoteky_uvery"],
      aliases: ["Wustenrot", "Wüstenrot stavebná sporiteľňa"] }),

  /* ——— Penzijní společnosti ——— */
  I({ id: "nn-ps", name: "NN Penzijní společnost", legalName: "NN Penzijní společnost",
      cats: ["pensionCompany"], produkty: ["penze"],
      partner: "NN Penzijní společnost", aliases: ["NN PS", "NN penzijní", "NN Penzijní"] }),
  I({ id: "conseq-ps", name: "Conseq penzijní společnost", legalName: "Conseq penzijní společnost",
      cats: ["pensionCompany"], produkty: ["penze"],
      partner: "Conseq penzijní společnost Doplňkové penzijní spoření nad 1000 Kč měsíčně",
      aliases: ["Conseq PS", "Conseq penzijní"] }),
  I({ id: "allianz-ps", name: "Allianz penzijní společnost", legalName: "Allianz penzijní společnost",
      cats: ["pensionCompany"], produkty: ["penze"],
      partner: "Allianz penzijní společnost Doplňkové penzijní spoření",
      aliases: ["Allianz PS", "Allianz penzijní"] }),
  I({ id: "csob-ps", name: "ČSOB Penzijní společnost", legalName: "ČSOB Penzijní společnost",
      cats: ["pensionCompany"], produkty: ["penze"],
      partner: "ČSOB Penzijní společnost", aliases: ["ČSOB PS", "ČSOB penzijní", "ČSOB Penzijní"] }),
  I({ id: "generali-ps", name: "Generali penzijní společnost", legalName: "Generali Penzijní společnost",
      cats: ["pensionCompany"], produkty: ["penze"],
      partner: "Generali Penzijní společnost Doplňkové penzijní spoření (dle výše příspěvku účastníka)",
      aliases: ["Generali PS", "Generali penzijní", "Generali ČS"] }),
  I({ id: "kb-ps", name: "KB Penzijní společnost", legalName: "KB penzijní společnost",
      cats: ["pensionCompany"], produkty: ["penze"],
      partner: "KB penzijní společnost", aliases: ["KB PS", "KB penzijní", "Komerční banka penzijní"] }),
  I({ id: "cs-ps", name: "Česká spořitelna – penzijní společnost", legalName: "ČS Penzijní společnost",
      cats: ["pensionCompany"], produkty: ["penze"],
      partner: "ČS Penzijní společnost", aliases: ["ČS PS", "ČS penzijní", "Česká spořitelna penzijní"] }),
  I({ id: "uniqa-ps", name: "UNIQA penzijní společnost",
      cats: ["pensionCompany"], produkty: ["penze"],
      aliases: ["UNIQA PS", "UNIQA penzijní", "Uniqa penzijní"],
      poznamka: "Zdroj názvu: frontend/src/lib/penze/fondy.js (data APS ČR). Partnerství nedoloženo." }),
  I({ id: "rentea-ps", name: "Rentea penzijní společnost",
      cats: ["pensionCompany"], produkty: ["penze"],
      aliases: ["Rentea", "Rentea PS"],
      poznamka: "Zdroj názvu: frontend/src/lib/penze/fondy.js (data APS ČR). Partnerství nedoloženo." }),

  /* ——— Pojišťovny ——— */
  I({ id: "allianz", name: "Allianz pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Allianz", aliases: ["Allianz"] }),
  I({ id: "csob-pojistovna", name: "ČSOB Pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "ČSOB Pojišťovna", aliases: ["ČSOB pojišťovna"] }),
  I({ id: "uniqa", name: "UNIQA pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Uniqa", aliases: ["UNIQA", "Uniqa"] }),
  I({ id: "nn", name: "NN Životní pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "NN", aliases: ["NN", "NN pojišťovna"] }),
  I({ id: "generali-ceska", name: "Generali Česká pojišťovna", legalName: "Generali Česká pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Generali Česká pojišťovna",
      aliases: ["Generali", "Generali Česká", "Česká pojišťovna", "GČP"] }),
  I({ id: "kooperativa", name: "Kooperativa pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      aliases: ["Kooperativa"] }),
  I({ id: "metlife", name: "MetLife",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Metlife", aliases: ["Metlife", "MetLife pojišťovna"] }),
  I({ id: "maxima", name: "Maxima pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Maxima", aliases: ["Maxima"] }),
  I({ id: "slavia", name: "Slavia pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Slavia", aliases: ["Slavia"] }),
  I({ id: "pillow", name: "Pillow pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Pillow", aliases: ["Pillow"] }),
  I({ id: "direct", name: "Direct pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Direct", aliases: ["Direct"] }),
  I({ id: "colonnade", name: "Colonnade Insurance",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Colonnade", aliases: ["Colonnade"] }),
  I({ id: "komercni-pojistovna", name: "Komerční pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Komerční pojišťovna", aliases: ["KP", "Komerční pojistovna"] }),
  I({ id: "hasicska", name: "Hasičská vzájemná pojišťovna", legalName: "Hasičská vzájemná",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Hasičská vzájemná", aliases: ["HVP", "Hasičská vzájemná"] }),
  I({ id: "pojistovna-vzp", name: "Pojišťovna VZP", legalName: "Pojišťovna VZP",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Pojišťovna VZP", aliases: ["PVZP"] }),
  I({ id: "sv-pojistovna", name: "SV pojišťovna",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "SV pojišťovna", aliases: ["SV"] }),
  I({ id: "axa-assistance", name: "AXA Assistance CZ", legalName: "AXA Assistance CZ",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "AXA Assistance CZ", aliases: ["AXA Assistance"] }),
  I({ id: "europ-assistance", name: "Europ Assistance", legalName: "Europ Assistance",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      partner: "Europ Assistance", aliases: ["Europ"] }),
  I({ id: "axa", name: "AXA", active: false,
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      aliases: ["AXA pojišťovna", "AXA penzijní společnost"],
      poznamka: "Značka AXA v ČR se v nabídce nepoužívá — ponecháno jen kvůli dohledání starých smluv (byla v COMMON_PROVIDERS v ContractEditor.js). Nástupce si musí poradce ověřit, katalog ho netvrdí." }),

  /* ——— Banky ——— */
  I({ id: "ceska-sporitelna", name: "Česká spořitelna",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty", "investice"],
      partner: "Česká spořitelna", aliases: ["ČS", "Ceska sporitelna"] }),
  I({ id: "komercni-banka", name: "Komerční banka",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty", "investice"],
      partner: "Komerční banka", aliases: ["KB"] }),
  I({ id: "csob", name: "ČSOB",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty", "investice"],
      partner: "ČSOB", aliases: ["Československá obchodní banka"] }),
  I({ id: "raiffeisenbank", name: "Raiffeisenbank",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty", "investice"],
      partner: "Raiffeisenbank", aliases: ["RB", "Raiffeisen bank"] }),
  I({ id: "unicredit", name: "UniCredit Bank",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty", "investice"],
      partner: "Unicreditbank", aliases: ["UniCredit", "Unicredit", "Unicreditbank"] }),
  I({ id: "mbank", name: "mBank",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty"],
      partner: "mBank", aliases: ["mbank"] }),
  I({ id: "oberbank", name: "Oberbank",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty"],
      partner: "Oberbank", aliases: ["OberBank"] }),
  I({ id: "hypotecni-banka", name: "Hypoteční banka",
      cats: ["bank"], produkty: ["hypoteky_uvery"],
      partner: "Hypoteční banka", aliases: ["HB"] }),
  I({ id: "trinity-bank", name: "Trinity Bank",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty"],
      partner: "Trinity Bank", aliases: ["Trinity"] }),
  I({ id: "moneta", name: "MONETA Money Bank",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty"],
      aliases: ["Moneta", "MONETA"] }),
  I({ id: "air-bank", name: "Air Bank",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty"],
      aliases: ["Airbank"] }),
  I({ id: "fio", name: "Fio banka",
      cats: ["bank", "securitiesBroker"], produkty: ["bankovni_ucty", "investice", "hypoteky_uvery"],
      aliases: ["Fio"] }),
  I({ id: "jt-banka", name: "J&T Banka",
      cats: ["bank", "investmentCompany"], produkty: ["bankovni_ucty", "investice"],
      aliases: ["J&T", "JT Banka"] }),
  I({ id: "equa", name: "Equa bank", active: false,
      cats: ["bank"], produkty: ["bankovni_ucty", "hypoteky_uvery"],
      aliases: ["Equa"],
      poznamka: "Značka se v nabídce nepoužívá — ponecháno jen kvůli dohledání starých smluv (byla v COMMON_PROVIDERS v ContractEditor.js)." }),

  /* ——— Spořitelní družstva a nebankovní úvěry ——— */
  I({ id: "artesa", name: "Artesa spořitelní družstvo", legalName: "Artesa spořitelní družstvo",
      cats: ["creditInstitution"], produkty: ["hypoteky_uvery", "bankovni_ucty"],
      partner: "Artesa spořitelní družstvo", aliases: ["Artesa"] }),
  I({ id: "acema", name: "ACEMA Credit Czech", legalName: "ACEMA Credit Czech",
      cats: ["creditInstitution"], produkty: ["hypoteky_uvery"],
      partner: "ACEMA Credit Czech", aliases: ["Acema"] }),

  /* ——— Leasing ——— */
  I({ id: "csob-leasing", name: "ČSOB Leasing", legalName: "ČSOB Leasing",
      cats: ["leasingCompany"], produkty: ["leasing", "hypoteky_uvery"],
      partner: "ČSOB Leasing", aliases: ["CSOB Leasing"] }),
  I({ id: "raiffeisen-leasing", name: "Raiffeisen Leasing", legalName: "Raiffeisen Leasing",
      cats: ["leasingCompany"], produkty: ["leasing", "hypoteky_uvery"],
      partner: "Raiffeisen Leasing", aliases: ["RL"] }),
  I({ id: "unicredit-leasing", name: "UniCredit Leasing", legalName: "Unicredit Leasing",
      cats: ["leasingCompany"], produkty: ["leasing", "hypoteky_uvery"],
      partner: "Unicredit Leasing", aliases: ["Unicredit Leasing"] }),

  /* ——— Investiční společnosti a obchodníci s CP ——— */
  I({ id: "conseq", name: "Conseq Investment Management",
      cats: ["investmentCompany"], produkty: ["investice"],
      partner: "Conseq", aliases: ["Conseq"] }),
  I({ id: "amundi", name: "Amundi",
      cats: ["investmentCompany"], produkty: ["investice"],
      partner: "Amundi", aliases: ["Amundi Czech Republic"] }),
  I({ id: "investika", name: "Investika",
      cats: ["investmentCompany"], produkty: ["investice"],
      partner: "Investika", aliases: ["INVESTIKA"] }),
  I({ id: "avant", name: "Avant investiční společnost",
      cats: ["investmentCompany"], produkty: ["investice"],
      partner: "Avant", aliases: ["Avant"] }),
  I({ id: "efekta", name: "Efekta investiční společnost",
      cats: ["investmentCompany"], produkty: ["investice"],
      partner: "Efekta", aliases: ["Efekta"] }),
  I({ id: "codya", name: "Codya investiční společnost",
      cats: ["investmentCompany"], produkty: ["investice"],
      partner: "Codya", aliases: ["Codya"] }),
  I({ id: "delta", name: "Delta investiční společnost",
      cats: ["investmentCompany"], produkty: ["investice"],
      partner: "Delta", aliases: ["Delta"] }),
  I({ id: "eic", name: "EIC",
      cats: ["investmentCompany"], produkty: ["investice"],
      partner: "EIC", aliases: ["EIC"],
      poznamka: "V BJ katalogu jen zkratka EIC — plný název nedoložen, proto legalName null." }),
  I({ id: "arts", name: "ARTS Asset Management",
      cats: ["investmentCompany"], produkty: ["investice"],
      partner: "ARTS", aliases: ["ARTS"] }),
  I({ id: "atris", name: "ATRIS investiční společnost", legalName: "ATRIS investiční společnost",
      cats: ["investmentCompany"], produkty: ["investice"],
      partner: "ATRIS investiční společnost", aliases: ["ATRIS", "Atris Realita"] }),
  I({ id: "kk-investment", name: "KK Investment Partners", legalName: "KK Investment Partners",
      cats: ["investmentCompany"], produkty: ["investice"],
      partner: "KK Investment Partners", aliases: ["KK Investment"] }),
  I({ id: "wood", name: "WOOD & Company", legalName: "WOOD & Company",
      cats: ["investmentCompany", "securitiesBroker"], produkty: ["investice"],
      partner: "WOOD & Company", aliases: ["Wood", "Wood and Company"] }),
  I({ id: "generali-investments", name: "Generali Investments CEE",
      cats: ["investmentCompany"], produkty: ["investice"],
      aliases: ["Generali Investments", "GIC"] }),
  I({ id: "csob-am", name: "ČSOB Asset Management",
      cats: ["investmentCompany"], produkty: ["investice"],
      aliases: ["ČSOB AM", "CSOB AM"] }),
  I({ id: "partners-is", name: "Partners investiční společnost",
      cats: ["investmentCompany"], produkty: ["investice"],
      aliases: ["Partners"],
      poznamka: "Zdroj názvu: frontend/src/data/fondyKatalog.js (fondy Partners). Partnerství nedoloženo." }),
  I({ id: "vanguard", name: "Vanguard",
      cats: ["investmentCompany"], produkty: ["investice"],
      country: "US", aliases: ["Vanguard (ETF)", "Vanguard ETF"] }),
  I({ id: "ishares", name: "iShares (BlackRock)",
      cats: ["investmentCompany"], produkty: ["investice"],
      country: "US", aliases: ["iShares", "iShares (ETF)", "BlackRock"] }),
  I({ id: "ibis-ingold", name: "Ibis InGold",
      cats: ["other"], produkty: ["investice"],
      partner: "Ibis InGold", aliases: ["InGold", "IBIS InGold"],
      poznamka: "Spoření ve zlatě — není investiční společnost ani obchodník s CP, proto kategorie other. Do našeptávače u investic se schválně nenabízí." }),
  I({ id: "fond-sance", name: "Fond Šance",
      cats: ["other"], produkty: ["investice"],
      aliases: ["Fond Sance"],
      poznamka: "Převzato ze seznamu INSTITUCE v AnalyzaV2Page.js. Není jasné, o kterou instituci jde — kategorie other, dokud to poradce nedoplní. Do našeptávače u investic se proto nenabízí." }),

  /* ——— Slovensko ——— */
  I({ id: "vub", name: "VÚB banka", country: "SK",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty", "investice"],
      aliases: ["VUB", "VÚB"] }),
  I({ id: "tatra-banka", name: "Tatra banka", country: "SK",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty", "investice"],
      aliases: ["Tatrabanka"] }),
  I({ id: "slovenska-sporitelna", name: "Slovenská sporiteľňa", country: "SK",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty", "investice"],
      aliases: ["SLSP", "Slovenska sporitelna"] }),
  I({ id: "csob-sk", name: "ČSOB SK", country: "SK",
      cats: ["bank"], produkty: ["hypoteky_uvery", "bankovni_ucty", "investice"],
      aliases: ["ČSOB Slovensko", "CSOB SK"] }),
  I({ id: "allianz-sk", name: "Allianz SK", country: "SK",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      aliases: ["Allianz Slovenská poisťovňa", "Allianz Slovensko"] }),
  I({ id: "nn-sk", name: "NN SK", country: "SK",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      aliases: ["NN Slovensko"],
      poznamka: "Slovenské penzijní pilíře sem schválně NEdoplňuji — v repu na to není zdroj a slovenský systém (DSS/DDS) je jiný než české DPS. Doplní poradce." }),
  I({ id: "generali-sk", name: "Generali SK", country: "SK",
      cats: ["insuranceCompany"], produkty: ["pojisteni"],
      aliases: ["Generali Slovensko"] }),
];

/** Rychlé vyhledání podle id. */
export const INSTITUCE_PODLE_ID = INSTITUCE.reduce((acc, i) => {
  acc[i.institutionId] = i;
  return acc;
}, {});

/**
 * Kategorie produktu pro daný typ.
 * Vrací { kategorie, znamy } — neznámý typ se NEsmí tvářit jako „nic nevyhovuje".
 */
export function kategorieProTyp(typ) {
  const k = String(typ ?? "").trim();
  if (!k) return { kategorie: null, znamy: true };
  if (!(k in TYP_PRODUKTU_NA_KATEGORII)) return { kategorie: null, znamy: false };
  return { kategorie: TYP_PRODUKTU_NA_KATEGORII[k], znamy: true };
}

/** Povolené kategorie institucí pro typ produktu. null = bez omezení. */
export function povoleneKategorieProTyp(typ) {
  const { kategorie } = kategorieProTyp(typ);
  if (!kategorie) return null;
  return POVOLENE_KATEGORIE[kategorie] || null;
}

/** Produktové kategorie instituce, které kvalifikují pro danou kategorii. */
export function pokrytiProKategorii(kategorie) {
  return POKRYTI_KATEGORIE[kategorie] || [kategorie];
}

/**
 * Smí se instituce nabídnout k tomuhle typu produktu?
 * Musí platit obojí: kategorie instituce je povolená A instituce vede některou
 * z produktových kategorií, které tu kategorii pokrývají (viz POKRYTI_KATEGORIE).
 */
export function jeInstitucePovolenaProTyp(inst, typ) {
  if (!inst) return false;
  const { kategorie } = kategorieProTyp(typ);
  if (!kategorie) return true;
  const povolene = POVOLENE_KATEGORIE[kategorie] || [];
  const pokryti = pokrytiProKategorii(kategorie);
  const sedikategorie = (inst.institutionCategories || []).some((c) => povolene.includes(c));
  const vedeProdukt = (inst.productCategories || []).some((p) => pokryti.includes(p));
  return sedikategorie && vedeProdukt;
}
